/* @flow */

/*
 * functions for translating between legacy editor API responses
 * and new domain objects
 */

import _ from 'lodash'
import parseDate from 'date-fns/parse'

import type {
  User,
  Customer,
  LegacyCustomer,
  Role,
  Settings,
  Poll,
  PollType,
  Icon,
  ExpertOpinion,
} from './types'

export const hardcodedRoles: Array<Role> = [
  {
    id: 'admin',
    name: 'Admin',
  },
  {
    id: 'library',
    name: 'Library',
  },
  {
    id: 'automation',
    name: 'Automation',
  },
  {
    id: 'analytics',
    name: 'Analytics',
  },
  {
    id: 'report',
    name: 'Report',
  },
  {
    id: 'revenue',
    name: 'Revenue',
  },
  {
    id: 'conversions',
    name: 'Conversions',
  },
  {
    id: 'dmp',
    name: 'DMP',
  },
  {
    id: 'api',
    name: 'API',
  },
  {
    id: 'audience',
    name: 'Audience',
  },
]

export const hardcodedPollTypes: Array<PollType> = [
  {
    id: 'one-axis',
    name: 'One Axis',
  },
  {
    id: 'two-axes',
    name: 'Two Axes',
  },
  {
    id: 'financial',
    name: 'Financial',
  },
  {
    id: 'ranking',
    name: 'Ranking',
  },
  {
    id: 'slider',
    name: 'Slider',
  },
]

export const hardcodedToolNameToPollTypeIdTable = {
  'one-axis': 'one-axis',
  '1-axis': 'one-axis',
  'two-axes': 'two-axes',
  '2-axis': 'two-axes',
  financial: 'financial',
  ranking: 'ranking',
  slider: 'slider',
}

export function getUserFromLegacyResponse(data: Object): User {
  let roleIds = []

  if (data.scopes) {
    roleIds = data.scopes.slice()
  }

  return {
    id: data.id,
    name: data.name,
    email: data.email || '',
    customerId: data.customerId || data.customer,
    roleIds,
    toolEnabled: data.toolEnabled || false,
    toolPollCreatorEnabled: data.toolPollCreatorEnabled || false,
  }
}

export function getSettingsFromLegacyLoginResponse(data: Object): Settings {
  return {
    language: data.language,
  }
}

export function getCustomerFromLegacy(customer: {
  name: string,
}): LegacyCustomer {
  const { name } = customer
  return {
    id: name,
    name,
  }
}

export function getCustomersFromLegacyEntities(
  data: Array<Object>
): Array<Customer> {
  return _(data)
    .map('customer')
    .uniq()
    .map(name => getCustomerFromLegacy({ name }))
    .value()
}

export function getIconFromLegacy(legacyIcon: Object): Icon {
  return {
    id: legacyIcon.id,
    name: legacyIcon.name,
    url: legacyIcon.url,
    customerId: legacyIcon.customer,
  }
}

export function getIconsFromLegacyIcons(
  legacyIcons: Array<Object>
): Array<Icon> {
  return legacyIcons.map(getIconFromLegacy)
}

export function getIconsFromLegacyPolls(
  legacyPolls: Array<Object>
): Array<Icon> {
  const icons = []

  legacyPolls.forEach(legacyPoll => {
    if (!Array.isArray(legacyPoll.mediaIcons)) return

    legacyPoll.mediaIcons.forEach(legacyIcon => {
      const iconUrl = legacyIcon.thumb
      icons.push({
        id: iconUrl,
        url: iconUrl,
        customerId: legacyPoll.customer,
        name: legacyIcon.name,
      })
    })
  })

  return icons
}

export function getExpertOpinionsFromLegacyPolls(
  legacyPolls: Array<Object>
): Array<ExpertOpinion> {
  const expertOpinions = []

  legacyPolls.forEach(legacyPoll => {
    if (!Array.isArray(legacyPoll.mediaIcons)) return

    legacyPoll.mediaIcons.forEach((legacyIcon, index) => {
      const iconUrl = legacyIcon.thumb
      const expertOpinionId = `${legacyPoll.id}#${index}`
      expertOpinions.push({
        id: expertOpinionId,
        text: legacyIcon.snippet,
        name: legacyIcon.name,
        x: legacyIcon.x,
        y: legacyIcon.y,
        iconId: iconUrl,
      })
    })
  })

  return expertOpinions
}

export function getPollsFromLegacyPolls(
  legacyPolls: Array<Object>
): Array<Poll> {
  const polls = []

  legacyPolls.forEach(legacyPoll => {
    const expertOpinionIds = []

    if (Array.isArray(legacyPoll.mediaIcons)) {
      legacyPoll.mediaIcons.forEach((legacyIcon, index) => {
        const expertOpinionId = `${legacyPoll.id}#${index}`
        expertOpinionIds.push(expertOpinionId)
      })
    }

    const type = legacyPoll.tool || legacyPoll.type

    if (hardcodedToolNameToPollTypeIdTable[type] == null) {
      console.warn(`
        no PollType found for name
        \`${type}\` for poll
        with id \`${legacyPoll.id}\`
        and name \`${legacyPoll.name}\`
      `)
      return
    }

    polls.push({
      id: legacyPoll.id,
      name: legacyPoll.name,
      featured: legacyPoll.featured,
      sponsored: legacyPoll.sponsored,
      automated: legacyPoll.automated,
      horizontal: legacyPoll.horizontal,
      adminOnlyEdit: legacyPoll.adminOnlyEdit,
      // TODO need to use moment here probably
      created: parseDate(legacyPoll.created),
      modified: parseDate(legacyPoll.last_edited),
      labelTop: legacyPoll.toplabel,
      labelBottom: legacyPoll.bottomlabel,
      labelLeft: legacyPoll.leftlabel,
      labelRight: legacyPoll.rightlabel,
      tags: legacyPoll.tags || [],
      header: legacyPoll.header,
      extrasteps: legacyPoll.extrasteps,
      designId: legacyPoll.defaultDesign,
      viewCount: legacyPoll.views,
      kind: hardcodedToolNameToPollTypeIdTable[type],
      customer: legacyPoll.customer,
      expertOpinionIds,
      options: legacyPoll.options,
      sliderUnit: legacyPoll.sliderUnit,
      sliderMax: legacyPoll.sliderMax,
      sliderMin: legacyPoll.sliderMin,
      sliderSteps: legacyPoll.sliderSteps,
      automationConfigured: legacyPoll.automationConfigured,
      automationSuggested: legacyPoll.automationSuggested,
      automationDisabled: legacyPoll.automationDisabled,
      isPartOfAnExperiment: legacyPoll.isPartOfAnExperiment,
      addToAutomationLibrary: legacyPoll.addToAutomationLibrary,
      dmpIntegration: legacyPoll.dmpIntegration,
    })
  })

  return polls.filter(z => z).reverse()
}

export function getLegacyExpertOpinionFromExpertOpinion(
  expertOpinion: ExpertOpinion
): Object {
  if (expertOpinion.icon == null) {
    throw new Error('expertOpinion must have embedded icon')
  }
  return {
    x: expertOpinion.x,
    y: expertOpinion.y,
    thumb: expertOpinion.icon.url,
    name: expertOpinion.name,
    snippet: expertOpinion.text,
  }
}

export function getLegacyPollFromPoll(poll: Poll): Object {
  if (poll.expertOpinions == null) {
    throw new Error('poll must have embedded expertOpinions')
  }

  let additionalData = {}
  switch (poll.kind) {
    case 'one-axis':
      additionalData = {
        entries: JSON.stringify(
          poll.expertOpinions.map(getLegacyExpertOpinionFromExpertOpinion)
        ),
        leftlabel: poll.labelLeft,
        rightlabel: poll.labelRight,
      }
      break
    case 'two-axes':
      additionalData = {
        entries: JSON.stringify(
          poll.expertOpinions.map(getLegacyExpertOpinionFromExpertOpinion)
        ),
        leftlabel: poll.labelLeft,
        rightlabel: poll.labelRight,
        toplabel: poll.labelTop,
        bottomlabel: poll.labelBottom,
      }
      break
    case 'ranking':
      additionalData = {
        options: JSON.stringify(poll.options),
      }
      break

    // slider
    case 'slider':
      additionalData = {
        sliderUnit: poll.sliderUnit,
        sliderMax: poll.sliderMax,
        sliderMin: poll.sliderMin,
        sliderSteps: poll.sliderSteps,
      }
      break

    default:
      break
    // do nothing
  }
  return {
    id: poll.id,
    name: poll.name,
    type: poll.kind,
    customer: poll.customer,
    header: poll.header,
    defaultDesign: poll.designId,
    featured: poll.featured,
    sponsored: poll.sponsored,
    automated: poll.automated,
    automationConfigured: poll.automationConfigured,
    automationSuggested: poll.automationSuggested,
    automationDisabled: poll.automationDisabled,
    horizontal: poll.horizontal,
    adminOnlyEdit: poll.adminOnlyEdit,
    verticals: JSON.stringify(poll.verticals),
    tags: JSON.stringify(poll.tags),
    addToAutomationLibrary: poll.addToAutomationLibrary,
    ...additionalData,
  }
}



// WEBPACK FOOTER //
// ./src/legacy.js